import { useEffect } from "react";

import { createPath, useLocation, useNavigate } from "react-router-dom";

import { LoadingSpinner } from "@/domain/ui/LoadingSpinner";

import { ForbiddenError } from "../errors/ForbiddenError";
import { useAuth } from "./AuthProvider";
import { DEVSPACE_ROLES } from "@supabase-devspace/constants";

interface Props {
  children: React.ReactNode;
}

export function AuthGuard({ children }: Props) {
  const { isAuthenticated, isLoading, isProfileLoading, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isLoginPage = location.pathname === "/auth/sign-in";
  const redirectUrl = createPath(location);

  const hasValidRole =
    userProfile?.role !== null &&
    userProfile?.role !== undefined &&
    (DEVSPACE_ROLES as readonly string[]).includes(userProfile.role);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isLoginPage) {
      navigate(`/auth/sign-in?redirect=${encodeURIComponent(redirectUrl)}`, {
        replace: true,
      });
    }

    if (!isLoading && isAuthenticated && isLoginPage) {
      const searchParams = new URLSearchParams(location.search);
      const redirect = searchParams.get("redirect") || "/games";
      navigate(redirect, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, isLoginPage, redirectUrl, location.search]);

  if ((isLoading || isProfileLoading) && !userProfile) {
    return (
      <div className="bg-background flex min-h-svh items-center justify-center">
        <div className="space-y-4 text-center">
          <LoadingSpinner />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (isLoginPage) {
    return <>{children}</>;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (isAuthenticated && !isLoading && !isProfileLoading && !hasValidRole) {
    return <ForbiddenError message="Invalid role." signOut={signOut} />;
  }

  return <>{children}</>;
}
